import React, { Fragment, useState, useEffect, useMemo, useRef } from "react";
import {
  addSelectOptionAndCombination,
  getFieldPath,
  onCommandEnter
} from "utils/form";
import { getFieldDeleteItemName } from "utils/builder";
import { FieldArray, useFormikContext } from "formik";
import { INPUT_CLASSES, UI_THEME } from "utils/constants/ui";
import UncontrolledInput from "components/UncontrolledInput";
// import OptionSetsSelect from "components/OptionSetsSelect";
import Select from "components/Form/fields/Select";
import Button from "components/Form/fields/Button";
import {
  DEFAULT_DEBOUNCE,
  FIELD_TEMPLATES,
  UUID_VALUE_KEY
} from "utils/constants/form";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
  listDragProps,
  LIST_DESTINATIONS,
  reorderModelsCollection
} from "utils/drag";
import DraggableOptionItem from "./DraggableOptionItem";
import { usePageBuilderContext } from "utils/context";
import { DRAG_SELECT_OPTION } from "utils/analytics";
import {
  getFormEntity,
  useFormCollections,
  useFormFieldLogicConfig,
  useIsManifestEditMode
} from "utils/hooks/manifest";
import get from "lodash/get";
import { reduceFieldSelectionRequirements } from "utils/combination";
import { STATE_KEYS } from "utils/constants/state";
import { useSelectOptions } from "utils/hooks/form";
import FieldListFactory from "components/FieldListFactory";
import HoverIconButton from "components/HoverIconButton";
import FieldLogicStatus from "components/FieldLogicStatus";

const AddSelectOptionInput = ({ onClick }) => {
  const ref = useRef(null);
  const [disabled, setDisabled] = useState(true);
  const addOption = () => {
    const label = ref && ref.current && ref.current.value;
    if (label) {
      onClick({ label });
      ref.current.value = "";
      setTimeout(() => setDisabled(true), 0);
    }
  };

  const inputProps = {
    placeholder: "New option",
    debounce: DEFAULT_DEBOUNCE,
    minRows: 1,
    innerRef: ref,
    valueSet: (val) => {
      if (disabled && val) {
        setDisabled(false);
      } else if (!disabled && !val) {
        setDisabled(true);
      }
    },
    onKeyDown: onCommandEnter(addOption)
  };

  return (
    <div className="flex flex-column items-end">
      <UncontrolledInput {...inputProps} />
      <div className="dib pt2">
        <Button
          disabled={disabled}
          copy="Add"
          theme={UI_THEME.SLIM}
          onClick={addOption}
        />
      </div>
    </div>
  );
};

const ValuesFields = ({ name, noBlocksStyle }) => {
  const {
    values,
    setValues,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const formEntity = getFormEntity(values);
  const formEntityUUID = formEntity && formEntity.uuid;

  const pageContext = usePageBuilderContext();
  const isEditMode = useIsManifestEditMode();
  const tracking = pageContext ? pageContext.tracking : {};

  const { combinations, fieldGroups, selectedFields } = useFormCollections();

  const uuidPath = getFieldPath(name, "uuid");
  const uuidValue = get(values, uuidPath, []);

  const logicConfig = useFormFieldLogicConfig({ name });

  const valueResultsMap = useMemo(
    () => reduceFieldSelectionRequirements({ combinations, uuid: uuidValue }),
    [combinations.length, uuidValue]
  );

  const optionsPath = getFieldPath(name, "options");
  const optionsValue = get(values, optionsPath, []);

  const labelPath = getFieldPath(name, "label");
  const labelValue = get(values, labelPath);
  const labelTouched = get(touched, labelPath);

  const defaultPath = getFieldPath(name, "default");
  const defaultValue = get(values, defaultPath);
  const defaultTouched = get(touched, defaultPath);

  const item = getFieldDeleteItemName(optionsPath);

  const optionsSetsPath = getFieldPath(
    name,
    STATE_KEYS.MULTISELECT.OPTIONS_SETS
  );
  const optionsSets = get(values, optionsSetsPath);

  const defaultOption = FIELD_TEMPLATES.SELECT.OPTION();
  const allSelectOptions = useSelectOptions({
    template: defaultOption,
    name,
    options: optionsValue
  });
  const hasOptions = Boolean(allSelectOptions.length > 1);

  /**
   * Unset default if:
   * - options are not present
   * - default value is present but its uuid is not in options
   * -- this can happen when transitioning between option sets
   */
  useEffect(() => {
    if (defaultValue) {
      if (!hasOptions) {
        setFieldValue(defaultPath, null);
      } else {
        const defaultMatch = allSelectOptions.find(
          ({ uuid }) => uuid === defaultValue
        );
        if (!defaultMatch) {
          setFieldValue(defaultPath, null);
        }
      }
    }
  }, [hasOptions, defaultValue, optionsSets]);

  const defaultSelectProps = {
    options: allSelectOptions,
    stateKey: defaultPath,
    value: defaultValue,
    valueKey: UUID_VALUE_KEY,
    disabled: !hasOptions || isEditMode,
    onChange: () => (evt) => {
      const selection = evt.currentTarget.value;
      const selectedValue =
        !selection || defaultOption.uuid === selection ? null : selection;
      // TODO set on page value?

      setFieldValue(defaultPath, selectedValue);
      if (!defaultTouched) {
        setFieldTouched(defaultPath, true);
      }
    },
    labelTransform: (val) => val,
    customClasses: {
      container: "w-100",
      input: INPUT_CLASSES
    }
  };
  const defaultOptionSelect = (
    <div className="w-100 pt3">
      <label className="f6 mb2 dib fw6">Default</label>
      <Select {...defaultSelectProps} />
    </div>
  );

  return (
    <Fragment>
      <FieldListFactory
        name={name}
        config={[
          {
            name: "label"
          },
          {
            name: "description"
          }
        ]}
      />
      {defaultOptionSelect}
      <div className="pt3">
        <FieldLogicStatus name={name} />
      </div>
      <FieldArray
        name={optionsPath}
        render={(arrayHelpers) => {
          const optionNodes = optionsValue.map((model, modelIx) => {
            const modelName = `${optionsPath}[${modelIx}]`;

            const modelProps = {
              name: modelName,
              model,
              modelIx: modelIx,
              /**
               * Use uuid for two purposes
               * - Drag and drop API
               * - Pass through to option component
               */
              uid: model.uuid,
              uuid: model.uuid,
              index: modelIx,
              arrayHelpers: arrayHelpers,
              item,
              dragDisabled: isEditMode,
              disabled: isEditMode,
              fieldName: name,
              valueResults: valueResultsMap[model.uuid],
              combinations
            };

            return (
              <DraggableOptionItem
                key={`${model.uuid}-${modelIx}`}
                {...modelProps}
              />
            );
          });

          const addOptionProps = {
            onClick: addSelectOptionAndCombination({
              arrayHelpers,
              setFieldValue,
              setFieldTouched,
              touched,
              name,
              fieldGroups,
              selectedFields,
              combinations
            })
          };

          return (
            <Fragment>
              <div className="flex flex-column pt3 mt4 bt hairline-1">
                <div className="flex flex-row justify-between">
                  <div className="f5 mb2 dib fw6">Options</div>
                </div>
                {/* <div className="pt2 pb3 flex flex-column">
                  <div className="f6 mb2 dib fw6">List</div>
                  <div className="f6 mb2 dib ">
                    Use a pre-defined list of options.
                  </div>
                  <OptionSetsSelect name={optionsSetsPath} />
                </div> */}
                <div className="pt2 pb3 flex flex-column">
                  {/* <div className="f6 mb2 dib fw6">Custom</div>
                  <div className="f6 mb2 dib ">Create your own options.</div> */}
                  <AddSelectOptionInput {...addOptionProps} />
                </div>
              </div>
              <DragDropContext
                onDragEnd={reorderModelsCollection({
                  setFieldValue,
                  setFieldTouched,
                  models: optionsValue,
                  modelsPath: optionsPath,
                  tracking: {
                    event: DRAG_SELECT_OPTION,
                    data: {
                      ...tracking
                    }
                  }
                })}
              >
                <Droppable droppableId={LIST_DESTINATIONS.OPTIONS}>
                  {(provided, snapshot) => (
                    <ul
                      {...listDragProps({
                        provided,
                        snapshot,
                        classes: "ma0 pa0 list w-100"
                      })}
                    >
                      {optionNodes}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            </Fragment>
          );
        }}
      />
    </Fragment>
  );
};

export default ValuesFields;
